<template>
  <v-container fluid grid-list-sm>
    <v-card class="pa-3 pt-5">
      <v-layout row wrap fill-height class="text-xs-left">
        <v-flex xs12 sm3 class="px-3">
          <v-text-field
            placeholder="New Supplier"
            v-model="supplier.name"
            :error-messages="nameErrors"
            label="Name"
            required
            :readonly="isReadOnly"
            @input="$v.supplier.name.$touch()"
            @blur="$v.supplier.name.$touch()"
            @change="onSupplierNameChange"
            :outlined="outlinedInputs"
            :clearable="!isReadOnly"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm2 class="px-2">
          <v-text-field
            v-model="supplier.code"
            :error-messages="codeErrors"
            label="Code"
            required
            :readonly="isReadOnly"
            @input="$v.supplier.code.$touch()"
            @blur="$v.supplier.code.$touch()"
            :outlined="outlinedInputs"
            :clearable="!isReadOnly"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm2 class="px-2">
          <service-type-list-select
            :supplier-type="
              supplier.serviceType === 'ACCOMMODATION'
                ? 'HOTELS'
                : supplier.serviceType
            "
            :service-type-name="supplier.typeName"
            customLabel="Type"
            @onSelect="onServiceTypeSelect($event)"
            :outlined="outlinedInputs"
          ></service-type-list-select>
        </v-flex>

        <v-flex xs12 sm3 class="px-3">
          <destination-list-select
            :destination="supplier.destination"
            @onSelect="onDestinationSelect($event)"
            :outlined="outlinedInputs"
            hide-not-in-use
          ></destination-list-select>
        </v-flex>
        <v-flex xs12 sm2 class="px-2">
          <language-list-select
            :language-id="supplier.languageId"
            @onSelect="onLanguageSelect($event)"
            :outlined="outlinedInputs"
          ></language-list-select>
        </v-flex>
        <v-flex xs12 sm2 class="px-2">
          <currency-list-select
            v-if="supplier && !supplier.isUsed"
            v-model="supplier.currencyCode"
            :outlined="outlinedInputs"
          ></currency-list-select>
          <v-text-field
            v-if="supplier && supplier.isUsed"
            v-model="supplier.currencyCode"
            :outlined="outlinedInputs"
            readonly
          >
          </v-text-field>
        </v-flex>
        <v-flex xs12 sm2 class="px-2">
          <rate-type-list-select
            customLabel="Default Rate Type"
            v-model="supplier.defaultRateTypeCode"
            :outlined="outlinedInputs"
            hideNotInUse
          ></rate-type-list-select>
        </v-flex>
        <v-flex xs12 sm2 class="px-2">
          <stars-list-select
            customLabel="Rating"
            v-model="supplier.rating"
            :outlined="outlinedInputs"
          ></stars-list-select>
        </v-flex>
        <v-flex xs12 sm3 class="px-3">
          <board-basis-list-select
            v-model="supplier.boardBasis"
            @onSelect="onBoardBasisSelect($event)"
            :outlined="outlinedInputs"
          ></board-basis-list-select>
        </v-flex>
        <v-flex xs12 sm3 class="px-3">
          <v-text-field
            label="Email"
            v-model="supplier.email"
            prepend-inner-icon="mdi-email"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm3 class="px-3">
          <v-text-field
            label="Email2"
            v-model="supplier.email2"
            type="email"
            prepend-innericon="mdi-email"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm3 class="px-3">
          <v-text-field
            label="Telephone"
            v-model="supplier.telephone"
            prepend-inner-icon="mdi-phone"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm3 class="px-3">
          <v-text-field
            label="Region"
            v-model="supplier.region"
            prepend-inner-icon="mdi-web"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm3 class="px-3">
          <head-office-list-select
            v-model="supplier.headOfficeId"
            :outlined="outlinedInputs"
            clearable
          ></head-office-list-select>
        </v-flex>
        <v-flex xs12 sm6 class="px-3">
          <v-text-field
            label="Pickup"
            v-model="supplier.pickup"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 class="px-3">
          <v-text-field
            label="Dropoff"
            v-model="supplier.dropoff"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex xs12 sm6 class="px-3">
          <tags-select
            v-model="supplier.tags"
            :outlined="outlinedInputs"
          ></tags-select>
        </v-flex>
        <v-flex xs12 sm6 class="px-3">
          <v-text-field
            label="Zone"
            v-model="supplier.zone"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex xs12 sm6 class="px-3">
          <v-textarea
            label="Address"
            v-model="supplier.address"
            :outlined="outlinedInputs"
          ></v-textarea>
        </v-flex>
        <v-flex xs12 sm6 class="px-3">
          <v-textarea
            label="Notes"
            v-model="supplier.notes"
            :outlined="outlinedInputs"
          ></v-textarea>
        </v-flex>
        <v-flex xs12 sm6 class="px-3">
          <v-text-field
            label="Coordinates (lat, lon)"
            v-model="supplier.geolocation"
            :outlined="outlinedInputs"
            append-icon="mdi-map-marker"
            @click:append="onCoordinatesClick"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 class="px-3">
          <v-text-field
            label="Website"
            v-model="supplier.website"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>

        <v-flex xs12 sm6 class="px-3">
          <v-text-field
            v-model="supplier.altName"
            label="Alternative Name"
            :outlined="outlinedInputs"
            clearable
          ></v-text-field>
        </v-flex>

        <v-flex xs12>
          <v-container px-4 fluid py-0>
            <v-row>
              <v-switch
                color="primary"
                label="Child Price as Extra"
                v-model="supplier.extrasChildPrice"
                class="mr-4"
                @change="onExtrasChildPriceChange"
              ></v-switch>
              <v-switch
                color="primary"
                label="Not In Use"
                v-model="supplier.notInUse"
              ></v-switch>

              <v-switch
                v-if="
                  $store.getters.hasAccessTo('suppliers:head_office')
                  //&& supplier.serviceType == 'OTHER'
                "
                color="primary"
                label="Is HeadOffice"
                v-model="supplier.isHeadOffice"
                class="ml-4"
              ></v-switch>
              <v-switch
                color="primary"
                label="No Letter"
                class="ml-4"
                v-model="supplier.noLetter"
              ></v-switch>

              <v-switch
                color="primary"
                label="No Voucher"
                class="ml-4"
                v-model="supplier.noVoucher"
              ></v-switch>
              <v-switch
                v-if="$store.getters.hasAccessTo('third_party:marketplace')"
                color="primary"
                label="Market Place"
                class="ml-4"
                v-model="supplier.marketPlace"
                @change="onMarketPlaceChange"
              ></v-switch>
            </v-row>
          </v-container>
        </v-flex>
      </v-layout>
    </v-card>
    <v-layout align-center column pt-3>
      <v-flex>
        <v-btn class="mr-2" @click="onContact"
          ><v-icon>mdi-email</v-icon>Contact</v-btn
        >
        <save-btn
          @click="onSave"
          :disabled="
            !isValid || $store.getters.hasAccessTo('suppliers:view_only')
          "
        />
      </v-flex>
    </v-layout>
    <div v-if="debug">
      {{ supplier }} -- {{ isReadOnly }} --
      {{ $store.getters.hasAccessTo("suppliers:supplier_update") }}
    </div>
    <email-dialog
      :show="showEmailDialog"
      v-model="email"
      @close="showEmailDialog = false"
      @send="onSendMessage"
    ></email-dialog>
    <coordinates-dialog
      :show="showCoordinatesMap"
      @close="showCoordinatesMap = false"
      @save="showCoordinatesMap = false"
      :address="supplier.address"
      v-model="supplier.geolocation"
    ></coordinates-dialog>
    <confirmation-dialog
      :showDialog="showChildExtraPriceConfirmation"
      @onConfirm="showChildExtraPriceConfirmation = false"
      @onCloseDialog="onCancelChildExtraPrice"
    >
      <template slot="message"
        >This will delete all Child and Infant prices from this
        Supplier.</template
      >
    </confirmation-dialog>
  </v-container>
</template>

<script>
import ServiceTypeListSelect from "../../gt/shared/ServiceTypeListSelect";
import StarsListSelect from "../../gt/shared/StarsListSelect";
import DestinationListSelect from "../../gt/shared/DestinationListSelect";
import LanguageListSelect from "../../gt/shared/LanguageListSelect";
import CurrencyListSelect from "../../gt/shared/CurrencyListSelect";
import RateTypeListSelect from "../../gt/shared/RateTypeListSelect";
import BoardBasisListSelect from "../../gt/shared/BoardBasisListSelect";
import HeadOfficeListSelect from "../../gt/shared/HeadOfficeListSelect";
import { required, maxLength, email } from "vuelidate/lib/validators";
import SaveBtn from "../shared/SaveButton";
import TagsSelect from "../shared/TagsSelect";
import CoordinatesDialog from "../shared/CoordinatesDialog";
import ConfirmationDialog from "../shared/ConfirmationDialog";
import EmailDialog from "../shared/EmailDialog.vue";
import { SEND_CLIENT_MESSAGE } from "../../../constants/graphql";

export default {
  name: "SupplierDetailsForm",
  components: {
    ServiceTypeListSelect,
    StarsListSelect,
    DestinationListSelect,
    LanguageListSelect,
    CurrencyListSelect,
    RateTypeListSelect,
    BoardBasisListSelect,
    HeadOfficeListSelect,
    SaveBtn,
    TagsSelect,
    CoordinatesDialog,
    ConfirmationDialog,
    EmailDialog,
  },
  props: {
    supplier: {
      type: Object,
      default: () => ({}),
    },
    tags: [],
    chips: [],
  },

  data() {
    return {
      debug: false,
      showCoordinatesMap: false,
      showChildExtraPriceConfirmation: false,
      childExtraPricesCb: null,
      showEmailDialog: false,
      email: null,
    };
  },

  methods: {
    onMarketPlaceChange(payload) {
      this.$emit("marketplace-change", payload);
    },
    onContact() {
      this.email = {
        emailTo: "",
        message: "",
        multiple: true,
        emailToLabel: "Supplier",
        ccTo: this.$store.getters.userEmail,
        recipients: [
          {
            label: `Default: ${this.supplier.email}`,
            value: this.supplier.email,
          },
          {
            label: `Accounts: ${this.supplier.accountsEmail}`,
            value: this.supplier.accountsEmail,
          },
        ],
      };
      this.showEmailDialog = true;
    },
    onSendMessage(payload) {
      this.$apollo
        .mutate({
          mutation: SEND_CLIENT_MESSAGE,
          variables: { input: { ...payload } },
        })
        .then((res) => {
          if (res.data.contactClient.success) {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Message Sent",
            });
            this.showEmailDialog = false;
          } else {
            //res.data.ContactClient.success
            this.$store.dispatch("showAppSnackbar", {
              msg: res.data.contactClient.error,
              color: "error",
            });
          }
        })
        .catch((err) => {
          //console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
          console.log(err);
        });
    },
    onServiceTypeSelect(serviceType) {
      this.supplier.typeName = serviceType.name;
    },
    onCancelChildExtraPrice() {
      this.showChildExtraPriceConfirmation = false;
      this.supplier.extrasChildPrice = false;
    },
    onExtrasChildPriceChange(event) {
      //console.log("extras child price change", event);
      if (event) {
        this.showChildExtraPriceConfirmation = true;
      }
    },
    onCoordinatesClick() {
      this.showCoordinatesMap = true;
    },
    onDestinationSelect(data) {
      this.supplier.destinationId = data.id;
    },
    onLanguageSelect(data) {
      this.supplier.languageId = data;
    },
    onCurrencySelect(data) {
      //console.log(data);
      this.supplier.currencyCode = data;
    },
    onRateTypeSelect(data) {
      this.supplier.defaultRateTypeCode = data.code;
    },
    onBoardBasisSelect(data) {
      this.supplier.boardBasis = data;
    },
    onHeadOfficeSelect(data) {
      this.supplier.headOfficeId = data.id;
    },
    onSave() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$emit("onSave");
      }
    },
    onSupplierNameChange(event) {
      //console.log(event, this.supplier);
      if (this.supplier.pickup.toString() == "") {
        this.supplier.pickup = this.supplier.name;
      }
      if (this.supplier.dropoff.toString() == "") {
        this.supplier.dropoff = this.supplier.name;
      }
    },
  },

  computed: {
    isNew() {
      return !this.supplier || !this.supplier.id;
    },
    isReadOnly() {
      return (
        this.supplier &&
        this.supplier.isUsed &&
        !this.$store.getters.hasAccessTo("suppliers:supplier_update")
      );
    },
    isValid() {
      return (
        this.supplier.name &&
        this.supplier.name != "" &&
        this.supplier.code &&
        this.supplier.code != "" &&
        this.supplier.defaultRateTypeCode &&
        this.supplier.defaultRateTypeCode != "" &&
        this.supplier.currencyCode &&
        this.supplier.currencyCode != ""
      );
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },

    emailErrors() {
      var errors = [];
      if (!this.$v.supplier.email.$dirty) return errors;
      !this.$v.supplier.email.email && errors.push("Must be valid e-mail");
      !this.$v.supplier.email.required && errors.push("Email is required");
      return errors;
    },
    nameErrors() {
      var errors = [];
      if (!this.$v.supplier.name.$dirty) return errors;
      !this.$v.supplier.name.required && errors.push("Name is required.");
      return errors;
    },
    codeErrors() {
      var errors = [];
      if (!this.$v.supplier.code.$dirty) return errors;
      !this.$v.supplier.code.required && errors.push("Code is required.");
      return errors;
    },
  },
  validations: {
    supplier: {
      name: { required },
      code: { required },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/SupplierDetailsForm.vue